import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NotFound } from "@/pages/ErrorPages";
import { buildAutoplayMutedSrc } from "@/lib/embeds";
import { ArrowLeft, Heart, Share2, Volume2, VolumeX, Play } from "lucide-react";
import { toast } from "sonner";

const SESSION_ID =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? `reels-${crypto.randomUUID()}`
    : `reels-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function ReelsFeed() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [reels, setReels] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [muted, setMuted] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);
  const reportedRef = useRef(new Set()); // dedupe view_start by reel id

  const loadPage = useCallback(async (cur) => {
    const params = new URLSearchParams({ limit: "5" });
    if (cur) params.set("cursor", cur);
    const { data } = await api.get(`/public/tenants/${slug}/reels?${params.toString()}`);
    return data;
  }, [slug]);

  useEffect(() => {
    let alive = true;
    setPageStatus("loading");
    loadPage(null)
      .then((d) => {
        if (!alive) return;
        setTenant(d.tenant);
        setReels(d.reels);
        setCursor(d.next_cursor);
        setHasMore(d.has_more);
        setPageStatus("ready");
      })
      .catch(() => alive && setPageStatus("not_found"));
    return () => { alive = false; };
  }, [loadPage]);

  // IntersectionObserver: activate the card with ≥60% visibility
  useEffect(() => {
    if (pageStatus !== "ready" || reels.length === 0) return;
    const root = containerRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = Number(entry.target.getAttribute("data-idx"));
            setActiveIdx(idx);
          }
        }
      },
      { root, threshold: [0.6] },
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [pageStatus, reels]);

  // Fire view_start for active reel + load more when near end
  useEffect(() => {
    const reel = reels[activeIdx];
    if (!reel) return;
    if (!reportedRef.current.has(reel.id)) {
      reportedRef.current.add(reel.id);
      api.post("/events/view", {
        tenant_slug: slug,
        content_item_id: reel.id,
        source: "reel",
        kind: "view_start",
        position_seconds: 0,
        duration_seconds: 0,
        session_id: SESSION_ID,
      }).catch(() => {});
    }
    if (hasMore && !loadingMore && activeIdx >= reels.length - 2) {
      setLoadingMore(true);
      loadPage(cursor).then((d) => {
        setReels((prev) => [...prev, ...d.reels]);
        setCursor(d.next_cursor);
        setHasMore(d.has_more);
      }).finally(() => setLoadingMore(false));
    }
  }, [activeIdx, reels, hasMore, loadingMore, cursor, loadPage, slug]);

  if (pageStatus === "loading") {
    return <div className="min-h-screen bg-black grid place-items-center text-white/40 text-sm uppercase tracking-widest">Loading…</div>;
  }
  if (pageStatus === "not_found" || !tenant) return <NotFound />;

  const themeColor = tenant.theme_color;

  const onShare = async (reel) => {
    const url = `${window.location.origin}${sp(`/r/${reel.slug}`)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="bg-black text-white" data-testid="reels-feed">
      {/* Top-left exit + tenant pill */}
      <div className="fixed top-3 left-3 z-30 flex items-center gap-2 pointer-events-auto">
        <button
          onClick={() => navigate(sp(`/home`))}
          className="grid place-items-center w-9 h-9 bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/10"
          aria-label="Back to home"
          data-testid="reels-exit"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <Link to={sp(``)} className="px-3 py-1.5 bg-black/60 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.3em] hover:text-white text-white/80">
          {tenant.name}
        </Link>
      </div>

      {/* Top-right mute toggle (global for the feed) */}
      <button
        onClick={() => setMuted((m) => !m)}
        className="fixed top-3 right-3 z-30 grid place-items-center w-9 h-9 bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/10"
        aria-label={muted ? "Unmute" : "Mute"}
        data-testid="reels-mute-toggle"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      {/* Snap-scroll container */}
      <div
        ref={containerRef}
        className="h-screen overflow-y-scroll snap-y snap-mandatory"
        style={{ scrollbarWidth: "none" }}
      >
        {reels.map((reel, idx) => (
          <div
            key={reel.id}
            ref={(el) => (cardRefs.current[idx] = el)}
            data-idx={idx}
            className="snap-start h-screen w-full grid place-items-center bg-black relative"
            data-testid={`reel-card-${reel.slug}`}
          >
            <div className="relative w-full max-w-[420px] aspect-[9/16] bg-black overflow-hidden border border-white/10" style={{ boxShadow: `0 0 80px -10px ${themeColor}66` }}>
              {idx === activeIdx ? (
                <iframe
                  key={`${reel.id}-${muted ? "m" : "u"}`}
                  src={buildAutoplayMutedSrc(reel.video_source) + (muted ? "" : "&mute=0")}
                  title={reel.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <div className="absolute inset-0">
                  <img
                    src={reel.thumbnail_url}
                    alt=""
                    className="w-full h-full object-cover opacity-70"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-0 grid place-items-center">
                    <Play className="w-10 h-10 text-white/70" />
                  </div>
                </div>
              )}

              {/* Bottom gradient + content */}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 inset-x-0 p-4 pr-16 z-10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">
                  {reel.creator_name || tenant.name}
                </div>
                <h3 className="mt-1 font-display font-bold text-xl tracking-tight">{reel.title}</h3>
                {reel.description && <p className="mt-1 text-xs text-white/65 font-light line-clamp-2">{reel.description}</p>}
                {reel.linked_content && (
                  <Link
                    to={sp(`/title/${reel.linked_content.slug}`)}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] transition-transform hover:-translate-y-0.5"
                    style={{ backgroundColor: themeColor, color: "#0a0a0a" }}
                    data-testid={`reel-watch-full-${reel.slug}`}
                  >
                    <Play className="w-3 h-3 fill-black" /> Watch full film: {reel.linked_content.title}
                  </Link>
                )}
              </div>

              {/* Right rail actions */}
              <div className="absolute right-2 bottom-24 flex flex-col gap-3 z-20">
                <button
                  onClick={() => toast("Liked (visual only)")}
                  className="grid place-items-center w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/10"
                  aria-label="Like"
                  data-testid={`reel-like-${reel.slug}`}
                >
                  <Heart className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onShare(reel)}
                  className="grid place-items-center w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/10"
                  aria-label="Share"
                  data-testid={`reel-share-${reel.slug}`}
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {!hasMore && reels.length > 0 && (
          <div className="snap-start h-screen w-full grid place-items-center text-white/50 text-sm">
            <div className="text-center">
              <div className="font-display font-bold text-2xl">You're all caught up.</div>
              <Link to={sp(`/home`)} className="mt-4 inline-block px-5 py-2.5 text-xs uppercase tracking-[0.25em] border border-white/15 hover:border-white/40">
                Back to {tenant.name}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
